'use client';

import { useState } from 'react';

import type { Cart } from '@/entities/cart';
import { PromoCode } from '@/features/apply-promo/client';
import { formatMoney, formatNumber } from '@/shared/lib/money';
import { ButtonLink } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import { ProgressBar } from '@/shared/ui/progress-bar';
import { TextButton } from '@/shared/ui/text-link';

import styles from './cart-summary.module.scss';

/** "Ваш заказ" (04B): free-delivery progress, preorder note, totals, promo code and checkout CTA. */
export function CartSummary({ cart }: { cart: Cart }) {
  const [more, setMore] = useState(false);
  const { delivery } = cart;
  return (
    <aside className={styles.root} aria-label="Ваш заказ">
      <h2 className={styles.title}>Ваш заказ</h2>
      <div className={styles.shipping}>
        <div className={styles['ship-title']}>
          <Icon name="truck" size={20} strokeWidth={1.4} />
          <span>
            {delivery.isFree
              ? 'Бесплатная доставка'
              : `Бесплатная доставка от ${formatMoney(delivery.freeThreshold)}`}
          </span>
        </div>
        <ProgressBar value={delivery.progress} label="Прогресс до бесплатной доставки" />
        {!delivery.isFree && (
          <span className={styles.left}>
            До бесплатной доставки осталось {formatMoney(delivery.remaining)}
          </span>
        )}
      </div>
      {cart.hasPreorder && (
        <div className={styles.preorder}>
          <Icon name="clock" size={16} strokeWidth={1.5} className={styles.clock} />
          <span>В заказе есть предзаказ. Все товары отправим вместе после его поступления.</span>
        </div>
      )}
      <dl className={styles.sums}>
        <div className={styles.sum}>
          <dt>Товары, {cart.itemsCount} шт.</dt>
          <dd>{formatMoney(cart.subtotal)}</dd>
        </div>
        {cart.discountTotal.amountMinor > 0 && (
          <div className={styles.sum}>
            <dt>Скидка</dt>
            <dd className={styles.discount}>−{formatMoney(cart.discountTotal)}</dd>
          </div>
        )}
        <div className={styles.sum}>
          <dt>Доставка</dt>
          <dd>{delivery.isFree ? 'Бесплатно' : 'Рассчитаем при оформлении'}</dd>
        </div>
        <div className={styles.sum}>
          <dt>Бонусы к начислению</dt>
          <dd>{formatNumber(cart.bonusAccrual)}</dd>
        </div>
      </dl>
      <PromoCode cart={cart} />
      <div className={styles.total}>
        <span className={styles['total-label']}>Итого</span>
        <span className={styles['total-value']}>{formatMoney(cart.total)}</span>
      </div>
      {cart.installment && (
        <div className={styles.parts}>
          <span>
            или {cart.installment.count} платежа по {formatMoney(cart.installment.amount)}
          </span>
          <TextButton
            tone="muted"
            className={styles.more}
            aria-expanded={more}
            onClick={() => setMore((value) => !value)}
          >
            Подробнее
          </TextButton>
        </div>
      )}
      {more && (
        <span className={styles.caption}>Условия оплаты частями покажем на шаге оплаты.</span>
      )}
      <ButtonLink href="/checkout" size="lg" fullWidth className={styles.cta}>
        Перейти к оформлению — {formatMoney(cart.total)}
      </ButtonLink>
    </aside>
  );
}
